import React, { useEffect, useRef, useState } from 'react';
import { Camera, Eye, EyeOff, Lock, User } from 'lucide-react';
import UserAvatar from '@/components/ipe/UserAvatar';
import { tutorialAPI } from '@/utils/tutorialApi';
import { IPE_UPLOAD_ACCEPT } from '@/utils/ipeImageUploadFormats';
import '@/styles/ipe/ipe-account-center.css';

const AVATAR_MAX_BYTES = 5 * 1024 * 1024;

export default function TutorialProfileModal({ user, open, onClose, onSave }) {
  const [username, setUsername] = useState('');
  const [avatarUrl, setAvatarUrl] = useState('');
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [uploadingAvatar, setUploadingAvatar] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const avatarInputRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    setUsername(user?.username || '');
    setAvatarUrl(user?.avatarUrl || '');
    setCurrentPassword('');
    setNewPassword('');
    setConfirmPassword('');
    setShowPassword(false);
    setError('');
  }, [open, user]);

  if (!open) return null;

  const handleAvatarPick = () => {
    if (uploadingAvatar || submitting) return;
    avatarInputRef.current?.click();
  };

  const handleAvatarChange = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      setError('请选择图片文件');
      return;
    }
    if (file.size > AVATAR_MAX_BYTES) {
      setError('头像图片不能超过 5MB');
      return;
    }
    setError('');
    setUploadingAvatar(true);
    try {
      const url = await tutorialAPI.uploadAvatar(file);
      if (!url) throw new Error('上传失败');
      setAvatarUrl(url);
    } catch (err) {
      setError(err.message || '头像上传失败');
    } finally {
      setUploadingAvatar(false);
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError('');

    const trimmedUsername = username.trim();
    if (!trimmedUsername) {
      setError('用户名不能为空');
      return;
    }

    const wantsPasswordChange = Boolean(newPassword || confirmPassword || currentPassword);
    if (wantsPasswordChange) {
      if (!currentPassword) {
        setError('修改密码请输入当前密码');
        return;
      }
      if (!newPassword) {
        setError('请输入新密码');
        return;
      }
      if (newPassword.length < 6) {
        setError('新密码至少 6 位');
        return;
      }
      if (newPassword !== confirmPassword) {
        setError('两次输入的新密码不一致');
        return;
      }
    }

    setSubmitting(true);
    try {
      const payload = {
        username: trimmedUsername,
        avatarUrl: avatarUrl || '',
      };
      if (wantsPasswordChange) {
        payload.currentPassword = currentPassword;
        payload.newPassword = newPassword;
        payload.confirmPassword = confirmPassword;
      }
      const data = await onSave(payload);
      onClose();
      return data;
    } catch (err) {
      setError(err.message || '保存失败');
    } finally {
      setSubmitting(false);
    }
  };

  const handleBackdropClick = () => {
    if (submitting || uploadingAvatar) return;
    onClose();
  };

  return (
    <div className="tutorial-profile-backdrop" role="presentation" onClick={handleBackdropClick}>
      <div
        className="tutorial-profile-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="tutorial-profile-title"
        onClick={(event) => event.stopPropagation()}
      >
        <h3 id="tutorial-profile-title">修改信息</h3>
        <p className="tutorial-profile-modal__hint">可更换头像、用户名与登录密码</p>

        <form className="tutorial-profile-form" onSubmit={handleSubmit}>
          <div className="tutorial-profile-form__avatar">
            <button
              type="button"
              className="tutorial-profile-form__avatar-btn"
              onClick={handleAvatarPick}
              disabled={uploadingAvatar || submitting}
              aria-label="更换头像"
            >
              <UserAvatar
                avatarUrl={avatarUrl}
                displayName={username}
                className="tutorial-profile-form__avatar-image"
                size={72}
              />
              <span className="tutorial-profile-form__avatar-overlay">
                {uploadingAvatar ? (
                  <span>上传中</span>
                ) : (
                  <Camera size={18} />
                )}
              </span>
            </button>
            <input
              ref={avatarInputRef}
              type="file"
              accept={IPE_UPLOAD_ACCEPT}
              className="tutorial-profile-form__avatar-input"
              onChange={handleAvatarChange}
            />
            <p className="tutorial-profile-form__avatar-tip">点击头像上传，支持 JPG / PNG / WebP</p>
          </div>

          <label className="tutorial-profile-form__field">
            <span>用户名</span>
            <span className="tutorial-profile-form__input-wrap">
              <User size={16} className="tutorial-profile-form__input-icon" />
              <input
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                placeholder="请输入用户名"
                autoComplete="username"
                required
              />
            </span>
          </label>

          <div className="tutorial-profile-form__section">
            <span className="tutorial-profile-form__section-title">修改密码（可选）</span>
            <label className="tutorial-profile-form__field">
              <span>当前密码</span>
              <span className="tutorial-profile-form__input-wrap tutorial-profile-form__input-wrap--password">
                <Lock size={16} className="tutorial-profile-form__input-icon" />
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={currentPassword}
                  onChange={(e) => setCurrentPassword(e.target.value)}
                  placeholder="如需改密码请填写"
                  autoComplete="current-password"
                />
                <button
                  type="button"
                  className="tutorial-profile-form__password-toggle"
                  onClick={() => setShowPassword((prev) => !prev)}
                  aria-label={showPassword ? '隐藏密码' : '显示密码'}
                  tabIndex={-1}
                >
                  {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </span>
            </label>
            <label className="tutorial-profile-form__field">
              <span>新密码</span>
              <span className="tutorial-profile-form__input-wrap tutorial-profile-form__input-wrap--password">
                <Lock size={16} className="tutorial-profile-form__input-icon" />
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder="至少 6 位"
                  autoComplete="new-password"
                  minLength={6}
                />
              </span>
            </label>
            <label className="tutorial-profile-form__field">
              <span>确认新密码</span>
              <span className="tutorial-profile-form__input-wrap tutorial-profile-form__input-wrap--password">
                <Lock size={16} className="tutorial-profile-form__input-icon" />
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="再次输入新密码"
                  autoComplete="new-password"
                  minLength={6}
                />
              </span>
            </label>
          </div>

          {error ? <p className="tutorial-profile-form__error">{error}</p> : null}

          <div className="tutorial-profile-form__actions">
            <button
              type="button"
              className="tutorial-manual__ghost-btn"
              disabled={submitting || uploadingAvatar}
              onClick={onClose}
            >
              取消
            </button>
            <button
              type="submit"
              className="tutorial-manual__primary-btn"
              disabled={submitting || uploadingAvatar}
            >
              {submitting ? '保存中…' : '保存'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
